import * as React from "react"
import { MdArrowForward, MdInfoOutline } from "react-icons/md"
import { Button, Spacer, Link, VisuallyHidden, Badge } from "gatsby-interface"
import {
  Subsection,
  SubsectionHeading,
  SubsectionDescription,
} from "./DangerZone.parts"
import { siteTransfer as text, ui as uiText } from "@modules/locales/default.js"
import { FormattedMessage, commonMessageTags } from "@modules/locales"
import { useWorkspaceDetailsShortcuts } from "@modules/organization/builds/hooks/useWorkspaceDetailsShortcuts"
import { getPathToOrgPricing } from "@modules/organization/shared/utils"
import { TransferSiteModal } from "./TransferSiteModal"
import { useSiteTransfer } from "../hooks/useSiteTransfer"
import { TransferSiteCancelModal } from "./TransferSiteCancelModal"
import { TransferSiteResendModal } from "./TransferSiteResendModal"
import { PermissionsDigest, CloudRole } from "@modules/graphql/types"

type TransferSiteProps = {
  siteId: string
  siteName: string
  organizationId: string
  permissions: PermissionsDigest
}

export function TransferSite({
  siteId,
  siteName,
  organizationId,
  permissions,
}: TransferSiteProps) {
  const siteTransfer = useSiteTransfer(siteId, { organizationId })

  const permissionToTransfer =
    permissions?.role === CloudRole.Owner ||
    permissions?.role === CloudRole.Admin

  const workspace = useWorkspaceDetailsShortcuts(organizationId)

  const isFeasible =
    (workspace.isOnAgencyPlan || workspace.isOnEnterprisePlan) &&
    permissionToTransfer
  const isNotAvailableForPlan =
    !workspace.isOnAgencyPlan && !workspace.isOnEnterprisePlan

  return (
    <React.Fragment>
      <Subsection>
        <SubsectionHeading>
          {text.headers.transferSite} <Spacer direction="horizontal" size={3} />
          <Link
            href={`https://gatsby.dev/site-settings-transfer-site`} // todo: go to Rebrandly and update the link https://app.rebrandly.com/links/21a892f1e58e495a9f1f41464099e121
            title={text.labels.learnAboutSiteTransfer}
          >
            <VisuallyHidden>
              {text.labels.learnAboutSiteTransfer}
            </VisuallyHidden>
            <MdInfoOutline />
          </Link>
          <Spacer direction="horizontal" size={3} />
          <Badge tone="SUCCESS" size="S" textVariant="DEFAULT">
            {uiText.labels.beta}
          </Badge>
        </SubsectionHeading>

        {isFeasible && !siteTransfer.pending && !siteTransfer.transfered && (
          <Button
            size="L"
            variant="SECONDARY"
            tone="DANGER"
            onClick={siteTransfer.openModal}
          >
            {text.labels.transferSite}
          </Button>
        )}

        {isFeasible && siteTransfer.pending && (
          <div>
            <Button
              size="L"
              variant="SECONDARY"
              tone="NEUTRAL"
              onClick={siteTransfer.openResendModal}
            >
              {text.labels.resendEmail}
            </Button>

            <Spacer direction="horizontal" size={5} />

            <Button
              size="L"
              variant="SECONDARY"
              tone="NEUTRAL"
              onClick={siteTransfer.openRevokeModal}
            >
              {text.labels.cancelTransfer}
            </Button>
          </div>
        )}

        {siteTransfer.transfered && (
          <SubsectionDescription>
            {text.messages.siteIsTransferred}
          </SubsectionDescription>
        )}

        {siteTransfer.pending && siteTransfer.sentTo && (
          <SubsectionDescription>
            <FormattedMessage<"email", "strong">
              values={{ email: siteTransfer.sentTo }} //
              message={text.messages.siteIsBeingTransferred}
              tags={commonMessageTags}
            />
          </SubsectionDescription>
        )}

        {!siteTransfer.pending && !siteTransfer.transfered && (
          <SubsectionDescription>
            {permissionToTransfer ? (
              <React.Fragment>
                {text.messages.transferThisSiteTo}{" "}
                {isNotAvailableForPlan && (
                  <React.Fragment>
                    {text.messages.notAvailableToYourPlan}{" "}
                    <Link to={getPathToOrgPricing(organizationId)}>
                      {text.labels.upgradePlan} <MdArrowForward />
                    </Link>
                  </React.Fragment>
                )}
              </React.Fragment>
            ) : (
              <React.Fragment>
                {text.messages.transferRestrictedToRoles}
              </React.Fragment>
            )}
          </SubsectionDescription>
        )}

        {siteTransfer.errorInCard && (
          <SubsectionDescription>
            {siteTransfer.errorInCard}
          </SubsectionDescription>
        )}
      </Subsection>

      <TransferSiteModal
        isOpen={siteTransfer.modalIsOpen}
        siteId={siteId}
        siteName={siteName}
        onClose={siteTransfer.closeModal}
        onTransfer={siteTransfer.create}
        transfering={siteTransfer.creating}
        error={siteTransfer.errorInModal}
      />

      <TransferSiteCancelModal
        isOpen={siteTransfer.revokeModalIsOpen}
        onDismiss={siteTransfer.closeRevokeModal}
        onConfirm={siteTransfer.revoke}
        loading={siteTransfer.revoking}
        error={siteTransfer.errorInModal}
      />

      <TransferSiteResendModal
        isOpen={siteTransfer.resendModalIsOpen}
        onDismiss={siteTransfer.closeResendModal}
        onConfirm={siteTransfer.resend}
        loading={siteTransfer.resending}
        error={siteTransfer.errorInModal}
        sentTo={siteTransfer.sentTo}
      />
    </React.Fragment>
  )
}
